import {
  FileText,
  Globe,
  StickyNote,
  Quote,
  Upload,
  Plus,
  Search,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ResearchItem } from '@/hooks/useResearch';
import { cn } from '@/lib/utils';

const typeIcons: Record<string, React.ElementType> = {
  note: StickyNote,
  upload: Upload,
  web_snapshot: Globe,
  quote: Quote,
};

const typeLabels: Record<string, string> = {
  note: 'Note',
  upload: 'Upload',
  web_snapshot: 'Web',
  quote: 'Quote',
};

interface UncitedResearchListProps {
  items: ResearchItem[];
  onQuickCite: (item: ResearchItem) => void;
  onAddWithDetails: (item: ResearchItem) => void;
}

export function UncitedResearchList({
  items,
  onQuickCite,
  onAddWithDetails,
}: UncitedResearchListProps) {
  if (items.length === 0) {
    return (
      <div className="py-8 text-center">
        <div className="h-12 w-12 mx-auto mb-3 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <Search className="h-6 w-6 text-emerald-500" />
        </div>
        <p className="text-muted-foreground font-medium">All research cited!</p>
        <p className="text-sm text-muted-foreground/70">
          Every research item has been referenced
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-500/10 p-2 rounded-lg">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>
          {items.length} research {items.length === 1 ? 'item has' : 'items have'} not been cited yet
        </span>
      </div>
      
      <div className="space-y-2">
        {items.map((item) => {
          const Icon = typeIcons[item.type] || FileText;
          
          return (
            <div 
              key={item.id}
              className="border rounded-lg p-3 hover:border-primary/30 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className={cn(
                  "h-8 w-8 rounded-md flex items-center justify-center shrink-0",
                  "bg-muted text-muted-foreground"
                )}>
                  <Icon className="h-4 w-4" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {item.source_author || item.title}
                  </p>
                  {item.source_author && (
                    <p className="text-xs text-muted-foreground truncate">
                      {item.title}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px]">
                      {typeLabels[item.type] || item.type}
                    </Badge>
                    {item.source_publication && (
                      <span className="text-xs text-muted-foreground truncate">
                        {item.source_publication}
                      </span>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 h-8 gap-1"
                  onClick={() => onQuickCite(item)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Cite
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
